import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CapacityDataService, ErrorResponse } from '@enp/platform-api-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, concatMap, iif, map, of, switchMap, tap } from 'rxjs';

import {
  mapAtcOverviewDataApiModelToStoreModel,
  mapCapacityGraphDataApiModelToStoreModel,
  mapCapacityOverviewDataApiModelToStoreModel,
  mapCreatePlannedOutageRequestStoreModelToApiModel,
  mapCreateUnplannedOutageRequestStoreModelToApiModel,
  mapGetAtcResponseApiModelToStoreModel,
  mapGetNtcOptionsResponseApiModelToStoreModel,
  mapGetNtcResponseApiModelToStoreModel,
  mapNtcOverviewDataApiModelToStoreModel,
  mapPlannedOutageDetailsApiModelToStoreModel,
  mapPlannedOutageOptionsApiModelToStoreModel,
  mapPlannedOutagesApiModelToStoreModel,
  mapSubmitAtcRequestStoreModelToApiModel,
  mapSubmitNtcRequestStoreModelToApiModel,
  mapUnplannedOutageDetailsApiModelToStoreModel,
  mapUnplannedOutageOptionsApiModelToStoreModel,
  mapUnplannedOutagesApiModelToStoreModel,
  mapUpdatePlannedOutageRequestStoreModelToApiModel,
  mapUpdateUnplannedOutageRequestStoreModelToApiModel,
} from '@domain/capacity';
import { mapErrorResponseApiModelToStoreModel } from '@domain/common';

import { RouteSegments } from '@shared/enums';
import { cancelOnAction } from '@shared/operators';
import { CustomDateService } from '@shared/pipes/custom-date';
import { isDefined } from '@shared/utils/functions';
import { startOfDay } from '@shared/utils/zoned-date';

import { CoreActions } from '@stores/core';
import { CrisisActions } from '@stores/crisis';

import { CapacityActions } from './capacity.actions';
import { fromCapacity } from './capacity.selector';
import {
  CapacityGetAtcOverviewStoreModel,
  CapacityGetAtcRequestStoreModel,
  CapacityGetCapacityOverviewStoreModel,
  CapacityGetNtcGraphStoreModel,
  CapacityGetNtcOverviewStoreModel,
  CapacityGetNtcRequestStoreModel,
  CapacityNtcGetPlannedOutagesStoreModel,
  CapacityNtcGetUnplannedOutagesStoreModel,
  CapacityNtcPlannedOutagesListStoreModel,
  CapacityNtcUnplannedOutagesListStoreModel,
  CapacitySubmitAtcRequestStoreModel,
  CapacitySubmitNtcRequestStoreModel,
  mapCapacitySubmitNtcRequestStoreModelToCapacityGetNtcRequestStoreModel,
} from './models';

@Injectable()
export class CapacityEffects {
  readonly #actions$ = inject(Actions);
  readonly #capacityDataService = inject(CapacityDataService);
  readonly #router = inject(Router);
  readonly #translateService = inject(TranslateService);
  readonly #store = inject(Store);
  readonly #customDateService = inject(CustomDateService);

  public getCapacityOverview$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getCapacityOverview),
      switchMap(({ request }) =>
        this.#capacityDataService.getCapacityOverview(...this.mapCapacityGetCapacityOverviewStoreModelToApiModel(request)).pipe(
          map((overview) =>
            CapacityActions.getCapacityOverviewSuccess({
              overview: mapCapacityOverviewDataApiModelToStoreModel(overview),
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getCapacityOverviewFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public getCapacityOverviewFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getCapacityOverviewFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getNtcOverview$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcOverview),
      switchMap(({ request }) =>
        this.#capacityDataService.getNtcOverview(...this.mapCapacityGetNtcOverviewStoreModelToApiModel(request)).pipe(
          map((overview) =>
            CapacityActions.getNtcOverviewSuccess({
              overview: mapNtcOverviewDataApiModelToStoreModel(overview),
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcOverviewFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public getNtcOverviewFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcOverviewFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public refreshNtcOverview$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.refreshNtcOverview),
      concatLatestFrom(() => [this.#store.select(fromCapacity.selectLastNtcOverviewRequestData$)]),
      switchMap(([_, request]) =>
        iif(
          () => isDefined(request),
          of(CapacityActions.getNtcOverview({ request: request! })),
          of(CapacityActions.noLastNtcOverviewRequestDataFound())
        )
      )
    );
  });

  public getCapacityGraph$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getCapacityGraph),
      switchMap(({ request }) =>
        this.#capacityDataService.getCapacityGraph(...this.mapCapacityGetNtcGraphStoreModelToApiModel(request)).pipe(
          map((capacityGraph) =>
            CapacityActions.getCapacityGraphSuccess({ capacityGraph: mapCapacityGraphDataApiModelToStoreModel(capacityGraph) })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getCapacityGraphFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public getCapacityGraphFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getCapacityGraphFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public refreshCapacityGraph$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.refreshCapacityGraph),
      concatLatestFrom(() => [this.#store.select(fromCapacity.selectLastCapacityGraphRequestData$)]),
      switchMap(([_, request]) =>
        iif(
          () => isDefined(request),
          of(CapacityActions.getCapacityGraph({ request: request! })),
          of(CapacityActions.noLastCapacityGraphRequestDataFound())
        )
      )
    );
  });

  public getNtcPlannedOutageOptions$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcPlannedOutageOptions),
      switchMap(() =>
        this.#capacityDataService.getPlannedOutageOptions().pipe(
          map((plannedOutageOptions) =>
            CapacityActions.getNtcPlannedOutageOptionsSuccess({
              plannedOutageOptions: mapPlannedOutageOptionsApiModelToStoreModel(plannedOutageOptions),
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcPlannedOutageOptionsFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public getNtcPlannedOutageOptionsFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcPlannedOutageOptionsFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getNtcUnplannedOutageOptions$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcUnplannedOutageOptions),
      switchMap(() =>
        this.#capacityDataService.getUnplannedOutageOptions().pipe(
          map((unplannedOutageOptions) =>
            CapacityActions.getNtcUnplannedOutageOptionsSuccess({
              unplannedOutageOptions: mapUnplannedOutageOptionsApiModelToStoreModel(unplannedOutageOptions),
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcUnplannedOutageOptionsFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public getNtcUnplannedOutageOptionsFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcUnplannedOutageOptionsFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getPlannedOutages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcPlannedOutages),
      switchMap(({ action }) =>
        this.#capacityDataService.getPlannedOutages(...this.mapCapacityNtcGetPlannedOutagesStoreModelToApiModel(action)).pipe(
          map((plannedOutages) =>
            CapacityActions.getNtcPlannedOutagesSuccess({
              plannedOutages: mapPlannedOutagesApiModelToStoreModel(plannedOutages.entries),
              total: plannedOutages.totalCount,
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcPlannedOutagesFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public refreshPlannedOutages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.refreshNtcPlannedOutages),
      concatLatestFrom(() => [this.#store.select(fromCapacity.selectPlannedOutages$)]),
      switchMap(([_, plannedOutages]) =>
        iif(
          () => !plannedOutages || !plannedOutages.action,
          EMPTY,
          of(CapacityActions.getNtcPlannedOutages({ action: plannedOutages!.action! }))
        )
      )
    );
  });

  public getPlannedOutagesFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcPlannedOutagesFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getNextPlannedOutages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNextNtcPlannedOutages),
      concatLatestFrom(() => [this.#store.select(fromCapacity.selectPlannedOutages$)]),
      map(([, plannedOutages]) => this.mapGetNextPlannedOutagesStoreModelToApiModel(plannedOutages)),
      concatMap(({ params, nextOffset }) =>
        !params || !nextOffset
          ? EMPTY
          : this.#capacityDataService.getPlannedOutages(...params).pipe(
              map((plannedOutages) =>
                CapacityActions.getNextNtcPlannedOutagesSuccess({
                  plannedOutages: mapPlannedOutagesApiModelToStoreModel(plannedOutages.entries),
                  total: plannedOutages.totalCount,
                  offset: nextOffset,
                })
              ),
              catchError(({ error }: { error: ErrorResponse }) =>
                of(CapacityActions.getNextNtcPlannedOutagesFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
              )
            )
      )
    );
  });

  public getNextPlannedOutagesFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNextNtcPlannedOutagesFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getUnplannedOutages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcUnplannedOutages),
      switchMap(({ action }) =>
        this.#capacityDataService.getUnplannedOutages(...this.mapCapacityNtcGetUnplannedOutagesStoreModelToApiModel(action)).pipe(
          map((unplannedOutages) =>
            CapacityActions.getNtcUnplannedOutagesSuccess({
              unplannedOutages: mapUnplannedOutagesApiModelToStoreModel(unplannedOutages.entries),
              total: unplannedOutages.totalCount,
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcUnplannedOutagesFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public getUnplannedOutagesFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcUnplannedOutagesFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getNextUnplannedOutages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNextNtcUnplannedOutages),
      concatLatestFrom(() => [this.#store.select(fromCapacity.selectUnplannedOutages$)]),
      map(([, unplannedOutages]) => this.mapGetNextUnplannedOutagesStoreModelToApiModel(unplannedOutages)),
      concatMap(({ params, nextOffset }) =>
        !params || !nextOffset
          ? EMPTY
          : this.#capacityDataService.getUnplannedOutages(...params).pipe(
              map((unplannedOutages) =>
                CapacityActions.getNextNtcUnplannedOutagesSuccess({
                  unplannedOutages: mapUnplannedOutagesApiModelToStoreModel(unplannedOutages.entries),
                  total: unplannedOutages.totalCount,
                  offset: nextOffset,
                })
              ),
              catchError(({ error }: { error: ErrorResponse }) =>
                of(CapacityActions.getNextNtcUnplannedOutagesFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
              )
            )
      )
    );
  });

  public getNextUnplannedOutagesFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNextNtcUnplannedOutagesFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getNtcPlannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcPlannedOutage),
      switchMap(({ id }) =>
        this.#capacityDataService.getPlannedOutage(id).pipe(
          switchMap((plannedOutage) => [
            CapacityActions.getNtcPlannedOutageSuccess({ plannedOutage: mapPlannedOutageDetailsApiModelToStoreModel(plannedOutage) }),
            CapacityActions.getNtcPlannedOutageOptions(),
          ]),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcPlannedOutageFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public getNtcPlannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcPlannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getNtcUnplannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcUnplannedOutage),
      switchMap(({ id }) =>
        this.#capacityDataService.getUnplannedOutage(id).pipe(
          switchMap((unplannedOutage) => [
            CapacityActions.getNtcUnplannedOutageSuccess({
              unplannedOutage: mapUnplannedOutageDetailsApiModelToStoreModel(unplannedOutage),
            }),
            CapacityActions.getNtcUnplannedOutageOptions(),
          ]),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcUnplannedOutageFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public getNtcUnplannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcUnplannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public deleteNtcPlannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.deleteNtcPlannedOutage),
      switchMap(({ id, refresh }) =>
        this.#capacityDataService.deletePlannedOutage(id).pipe(
          map(() => CapacityActions.deleteNtcPlannedOutageSuccess({ id, refresh })),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.deleteNtcPlannedOutageFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public deleteNtcPlannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.deleteNtcPlannedOutageSuccess),
      tap(() => this.#router.navigate([RouteSegments.Capacity, RouteSegments.OutageManagement, RouteSegments.PlannedOutages])),
      switchMap(({ refresh }) => (refresh ? of(CapacityActions.refreshNtcPlannedOutages()) : EMPTY))
    );
  });

  public successToastOnDeleteNtcPlannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.deleteNtcPlannedOutageSuccess),
      switchMap(() => this.#translateService.get('capacity.outage-management.tabs.planned-outages.create-edit.toasts.delete-success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public deleteNtcPlannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.deleteNtcPlannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public cancelNtcPlannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.cancelNtcPlannedOutage),
      switchMap(({ id }) =>
        this.#capacityDataService.cancelPlannedOutage(id).pipe(
          map(() => CapacityActions.cancelNtcPlannedOutageSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.cancelNtcPlannedOutageFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public cancelNtcPlannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.cancelNtcPlannedOutageSuccess),
      switchMap(() => of(CapacityActions.refreshNtcPlannedOutages()))
    );
  });

  public successToastOnCancelNtcPlannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.cancelNtcPlannedOutageSuccess),
      switchMap(() => this.#translateService.get('capacity.outage-management.tabs.planned-outages.create-edit.toasts.cancel-success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public cancelNtcPlannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.cancelNtcPlannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public cancelNtcUnplannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.cancelNtcUnplannedOutage),
      switchMap(({ id }) =>
        this.#capacityDataService.cancelUnplannedOutage(id).pipe(
          map(() => CapacityActions.cancelNtcUnplannedOutageSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.cancelNtcUnplannedOutageFail({ errorCode: error.code, errorMsg: error.message ?? '' }))
          )
        )
      )
    );
  });

  public cancelNtcUnplannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.cancelNtcUnplannedOutageSuccess),
      switchMap(() => this.#translateService.get('capacity.outage-management.tabs.unplanned-outages.cancel-outage.toasts.success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public navigateToCapacityOverviewOnCancelNtcUnplannedOutageSuccess$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(CapacityActions.cancelNtcUnplannedOutageSuccess),
        tap(() => this.#router.navigate([RouteSegments.Root, RouteSegments.Capacity, RouteSegments.CapacityOverview]))
      );
    },
    { dispatch: false }
  );

  public cancelNtcUnplannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.cancelNtcUnplannedOutageFail),
      switchMap(({ errorCode, errorMsg }) =>
        this.#translateService.get('capacity.outage-management.tabs.unplanned-outages.cancel-outage.toasts.error', {
          code: errorCode,
          message: errorMsg,
        })
      ),
      map((errorMsg) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public createNtcPlannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.createNtcPlannedOutage),
      switchMap(({ request }) =>
        this.#capacityDataService.createPlannedOutage(mapCreatePlannedOutageRequestStoreModelToApiModel(request)).pipe(
          map(() => CapacityActions.createNtcPlannedOutageSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.createNtcPlannedOutageFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public createNtcPlannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.createNtcPlannedOutageSuccess),
      tap(() => this.#router.navigate([RouteSegments.Capacity, RouteSegments.OutageManagement, RouteSegments.PlannedOutages])),
      switchMap(() => this.#translateService.get('capacity.outage-management.tabs.planned-outages.create-edit.toasts.create-success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public createNtcPlannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.createNtcPlannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public createNtcUnplannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.createNtcUnplannedOutage),
      switchMap(({ request }) =>
        this.#capacityDataService.createUnplannedOutage(mapCreateUnplannedOutageRequestStoreModelToApiModel(request)).pipe(
          map(() => CapacityActions.createNtcUnplannedOutageSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.createNtcUnplannedOutageFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public createNtcUnplannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.createNtcUnplannedOutageSuccess),
      tap(() => this.#router.navigate([RouteSegments.CrisisManagement])),
      map(() => CrisisActions.checkCrisis())
    );
  });

  public createNtcUnplannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.createNtcUnplannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public updateNtcPlannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcPlannedOutage),
      switchMap(({ id, request }) =>
        this.#capacityDataService.updatePlannedOutage(id, mapUpdatePlannedOutageRequestStoreModelToApiModel(request)).pipe(
          map(() => CapacityActions.updateNtcPlannedOutageSuccess()),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.updateNtcPlannedOutageFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public updateNtcPlannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcPlannedOutageSuccess),
      tap(() => this.#router.navigate([RouteSegments.Capacity, RouteSegments.OutageManagement, RouteSegments.PlannedOutages])),
      switchMap(() => this.#translateService.get('capacity.outage-management.tabs.planned-outages.create-edit.toasts.edit-success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public updateNtcPlannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcPlannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public updateNtcUnplannedOutage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcUnplannedOutage),
      switchMap(({ id, request, connectedToCrisis }) =>
        this.#capacityDataService.updateUnplannedOutage(id, mapUpdateUnplannedOutageRequestStoreModelToApiModel(request)).pipe(
          map(() => CapacityActions.updateNtcUnplannedOutageSuccess({ connectedToCrisis })),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.updateNtcUnplannedOutageFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public updateNtcUnplannedOutageSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcUnplannedOutageSuccess),
      tap(({ connectedToCrisis }) => {
        if (!connectedToCrisis) {
          this.#router.navigate([RouteSegments.CrisisManagement]);
        }
      }),
      switchMap(() => this.#translateService.get('capacity.outage-management.tabs.unplanned-outages.create-edit.toasts.edit-success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public updateNtcUnplannedOutageFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcUnplannedOutageFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public reloadOutageDetailsOnSaveSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.updateNtcUnplannedOutageSuccess),
      map(() =>
        CrisisActions.getOutageDetails({
          isPolling: false,
        })
      )
    );
  });

  public getNtcOptions$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcOptions),
      switchMap(() =>
        this.#capacityDataService.getNtcOptions().pipe(
          map((getNtcOptionsResponse) =>
            CapacityActions.getNtcOptionsSuccess({
              getNtcOptionsResponse: mapGetNtcOptionsResponseApiModelToStoreModel(getNtcOptionsResponse),
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcOptionsFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      ),
      cancelOnAction(this.#actions$, CapacityActions.clearNtc)
    );
  });

  public getNtcOptionsFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcOptionsFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getNtc$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtc),
      switchMap(({ request }) =>
        this.#capacityDataService.getNtc(...this.mapCapacityGetNtcRequestStoreModelToApiModel(request)).pipe(
          map((getNtcResponse) => CapacityActions.getNtcSuccess({ getNtcResponse: mapGetNtcResponseApiModelToStoreModel(getNtcResponse) })),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getNtcFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      ),
      cancelOnAction(this.#actions$, CapacityActions.clearNtc)
    );
  });

  public getNtcFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getNtcFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public submitNtc$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.submitNtc),
      switchMap(({ request, reloadListOnSuccess }) =>
        this.#capacityDataService.submitNtc(...this.mapCapacitySubmitNtcRequestStoreModelToApiModel(request)).pipe(
          map(() => CapacityActions.submitNtcSuccess({ request, reloadListOnSuccess })),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.submitNtcFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public submitNtcSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.submitNtcSuccess),
      switchMap(() => this.#translateService.get('capacity.ntc-management.submit-success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public reloadNtcOnSubmitNtcSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.submitNtcSuccess),
      switchMap(({ request, reloadListOnSuccess }) => {
        if (reloadListOnSuccess) {
          return of(CapacityActions.getNtc({ request: mapCapacitySubmitNtcRequestStoreModelToCapacityGetNtcRequestStoreModel(request) }));
        }
        return EMPTY;
      })
    );
  });

  public submitNtcFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.submitNtcFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public getAtc$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getAtc),
      switchMap(({ request }) =>
        this.#capacityDataService.getAtc(...this.mapCapacityGetAtcRequestStoreModelToApiModel(request)).pipe(
          map((getAtcResponse) => CapacityActions.getAtcSuccess({ getAtcResponse: mapGetAtcResponseApiModelToStoreModel(getAtcResponse) })),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getAtcFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      ),
      cancelOnAction(this.#actions$, CapacityActions.clearAtc)
    );
  });

  public getAtcFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getAtcFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public submitAtc$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.submitAtc),
      switchMap(({ request }) =>
        this.#capacityDataService.submitAtc(...this.mapCapacitySubmitAtcRequestStoreModelToApiModel(request)).pipe(
          map(() => CapacityActions.submitAtcSuccess({ request })),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.submitAtcFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      )
    );
  });

  public submitAtcSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.submitAtcSuccess),
      switchMap(() => this.#translateService.get('capacity.atc-management.submit-success')),
      map((message: string) => CoreActions.setSuccessToastMessage({ toast: { message } }))
    );
  });

  public navigateToAtcOverviewOnSubmitAtcSuccess$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(CapacityActions.submitAtcSuccess),
        tap(({ request }) =>
          this.#router.navigate([RouteSegments.Capacity, RouteSegments.AtcOverview], {
            queryParams: { deliveryDay: this.#customDateService.transformToApiModel(request.deliveryDayDateTime) },
          })
        )
      );
    },
    { dispatch: false }
  );

  public getAtcOverview$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getAtcOverview),
      switchMap(({ request }) =>
        this.#capacityDataService.getAtcOverview(...this.mapCapacityGetAtcOverviewStoreModelToApiModel(request)).pipe(
          map((overview) =>
            CapacityActions.getAtcOverviewSuccess({
              overview: mapAtcOverviewDataApiModelToStoreModel(overview),
            })
          ),
          catchError(({ error }: { error: ErrorResponse }) =>
            of(CapacityActions.getAtcOverviewFail({ errorMsg: mapErrorResponseApiModelToStoreModel(error) }))
          )
        )
      ),
      cancelOnAction(this.#actions$, CapacityActions.clearAtcOverview)
    );
  });

  public getAtcOverviewFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.getAtcOverviewFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public submitAtcFail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.submitAtcFail),
      map(({ errorMsg }) => CoreActions.setErrorToastMessage({ toast: { message: errorMsg } }))
    );
  });

  public refreshAtcOverview$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(CapacityActions.refreshAtcOverview),
      concatLatestFrom(() => [this.#store.select(fromCapacity.selectLastAtcOverviewRequestData$)]),
      switchMap(([_, request]) =>
        iif(
          () => isDefined(request),
          of(CapacityActions.getAtcOverview({ request: request!, refresh: true })),
          of(CapacityActions.noLastAtcOverviewRequestDataFound())
        )
      )
    );
  });

  private mapCapacityGetNtcOverviewStoreModelToApiModel(request: CapacityGetNtcOverviewStoreModel) {
    return [this.#customDateService.transformToApiModel(request.deliveryDay)] as const;
  }

  private mapCapacityGetCapacityOverviewStoreModelToApiModel(request: CapacityGetCapacityOverviewStoreModel) {
    return [this.#customDateService.transformToApiModel(request.deliveryDayDate), request.mtuSize] as const;
  }

  private mapCapacityGetNtcGraphStoreModelToApiModel(request: CapacityGetNtcGraphStoreModel) {
    return [
      this.#customDateService.transformToApiModel(request.deliveryFromDateTime),
      this.#customDateService.transformToApiModel(request.deliveryToDateTime),
    ] as const;
  }

  private mapCapacityNtcGetPlannedOutagesStoreModelToApiModel(request: CapacityNtcGetPlannedOutagesStoreModel) {
    return [
      request.limit,
      request.offset,
      request.sortBy,
      this.#customDateService.transformToApiModel(request.deliveryDayFromDateTime),
      this.#customDateService.transformToApiModel(startOfDay(request.deliveryDayToDateTime)),
    ] as const;
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  private mapCapacityNtcGetUnplannedOutagesStoreModelToApiModel(request: CapacityNtcGetUnplannedOutagesStoreModel) {
    return [
      request.limit,
      request.offset,
      request.sortBy,
      this.#customDateService.transformToApiModel(request.deliveryDayFromDateTime),
      this.#customDateService.transformToApiModel(startOfDay(request.deliveryDayToDateTime)),
    ] as const;
  }

  private mapGetNextPlannedOutagesStoreModelToApiModel(plannedOutages?: CapacityNtcPlannedOutagesListStoreModel) {
    if (!plannedOutages) return {};
    const { action, offset, totalEntries, entries } = plannedOutages;
    if (!action || offset === undefined || !totalEntries || !entries || totalEntries <= entries.length) return {};
    const nextOffset = offset + action.limit;

    return {
      params: [
        action.limit,
        nextOffset,
        action.sortBy,
        this.#customDateService.transformToApiModel(action.deliveryDayFromDateTime),
        this.#customDateService.transformToApiModel(startOfDay(action.deliveryDayToDateTime)),
      ] as const,
      nextOffset,
    };
  }

  private mapGetNextUnplannedOutagesStoreModelToApiModel(unplannedOutages?: CapacityNtcUnplannedOutagesListStoreModel) {
    if (!unplannedOutages) return {};
    const { action, offset, totalEntries, entries } = unplannedOutages;
    if (!action || offset === undefined || !totalEntries || !entries || totalEntries <= entries.length) return {};
    const nextOffset = offset + action.limit;

    return {
      params: [
        action.limit,
        nextOffset,
        action.sortBy,
        this.#customDateService.transformToApiModel(action.deliveryDayFromDateTime),
        this.#customDateService.transformToApiModel(startOfDay(action.deliveryDayToDateTime)),
      ] as const,
      nextOffset,
    };
  }

  private mapCapacityGetNtcRequestStoreModelToApiModel(request: CapacityGetNtcRequestStoreModel) {
    return [
      this.#customDateService.transformToApiModel(request.deliveryDayDateTime),
      request.timescale,
      request.originator,
      request.borderDirection,
    ] as const;
  }

  private mapCapacitySubmitNtcRequestStoreModelToApiModel(request: CapacitySubmitNtcRequestStoreModel) {
    return [
      this.#customDateService.transformToApiModel(request.deliveryDayDateTime),
      request.timescale,
      request.originator,
      request.borderDirection,
      mapSubmitNtcRequestStoreModelToApiModel(request.submitNtcRequest),
    ] as const;
  }

  private mapCapacityGetAtcRequestStoreModelToApiModel(request: CapacityGetAtcRequestStoreModel) {
    return [this.#customDateService.transformToApiModel(request.deliveryDayDateTime), request.timescale] as const;
  }

  private mapCapacitySubmitAtcRequestStoreModelToApiModel(request: CapacitySubmitAtcRequestStoreModel) {
    return [
      this.#customDateService.transformToApiModel(request.deliveryDayDateTime),
      request.timescale,
      mapSubmitAtcRequestStoreModelToApiModel(request.submitAtcRequest),
    ] as const;
  }

  private mapCapacityGetAtcOverviewStoreModelToApiModel(request: CapacityGetAtcOverviewStoreModel) {
    return [this.#customDateService.transformToApiModel(request.deliveryDayDate)] as const;
  }
}
